import { motion } from "framer-motion";
import { Trash2, Eye, CalendarDays, Loader2, Share2, CalendarClock, Music, Info } from "lucide-react";
import { Button } from "./ui/button";
import { CalendarMenu } from "./CalendarMenu";
import { CrowdPick } from "./CrowdPick";

export function prettyDate(d) {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return d;
  }
}

export function NightCard({ night, index, share, busy, onView, onShare, onReschedule, onDelete, onApplyVote, toPlan }) {
  return (
    <motion.div
      data-testid={`saved-night-${index}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      className="rounded-2xl border border-border bg-card p-6 flex flex-col"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold">{night.city}</h3>
        <span className="font-display font-bold neon-magenta">{night.budget}</span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-1.5" data-testid={`night-date-${index}`}>
          <CalendarDays size={14} /> {prettyDate(night.date)}
        </span>
        <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold">{night.vibe}</span>
        {night.event_found ? (
          <span className="inline-flex items-center gap-1 text-xs neon-cyan" data-testid={`night-has-event-${index}`}>
            <Music size={12} /> Live event
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`night-no-event-${index}`}>
            <Info size={12} /> Dinner &amp; drinks
          </span>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {night.stops.map((s, si) => (
          <div key={`${s.type}-${s.name}`} className="flex items-center gap-2 text-sm">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-secondary text-[11px] font-bold">{si + 1}</span>
            <span className="truncate" data-testid={`night-${index}-stop-${si}`}>{s.name}</span>
            <span className="ml-auto shrink-0 text-xs text-muted-foreground">{s.time_label}</span>
          </div>
        ))}
      </div>

      <CrowdPick share={share} nightIndex={index} applyingType={busy?.startsWith("apply:") ? busy.slice(6) : null} onApply={(t) => onApplyVote(night, t)} />

      <div className="mt-5 flex flex-wrap gap-2">
        <Button data-testid={`view-night-${index}`} size="sm" onClick={() => onView(night)} className="flex-1 rounded-full font-semibold">
          <Eye size={15} className="mr-1.5" /> View
        </Button>
        <Button
          data-testid={`share-night-${index}`}
          size="sm"
          variant="outline"
          disabled={busy === "share"}
          onClick={() => onShare(night)}
          className="rounded-full"
          title="Copy share link"
        >
          {busy === "share" ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />}
        </Button>
        <Button
          data-testid={`reschedule-night-${index}`}
          size="sm"
          variant="outline"
          onClick={() => onReschedule(night)}
          className="rounded-full"
          title="Reschedule"
        >
          <CalendarClock size={15} />
        </Button>
        <CalendarMenu plan={toPlan(night)} size="sm" iconOnly testId={`calendar-night-${index}`} />
        <Button
          data-testid={`delete-night-${index}`}
          size="sm"
          variant="outline"
          onClick={() => onDelete(night.id)}
          className="rounded-full"
        >
          <Trash2 size={15} />
        </Button>
      </div>
    </motion.div>
  );
}
